import React from 'react';
import { Link } from 'react-router-dom';
import { 
  LogIn, 
  UserPlus, 
  User, 
  Shield, 
  ArrowRight, 
  CheckCircle,
  Zap,
  Lock
} from 'lucide-react';

const AuthLandingPage = () => {
  const features = [
    {
      icon: <UserPlus className="w-6 h-6 text-[#0066A3]" />,
      title: 'Easy Registration',
      description: 'Create your account in minutes with our streamlined signup process'
    },
    {
      icon: <Shield className="w-6 h-6 text-green-600" />,
      title: 'Secure Authentication',
      description: 'Advanced security features including password management and session control'
    },
    {
      icon: <User className="w-6 h-6 text-[#0066A3]" />,
      title: 'Profile Management',
      description: 'Complete control over your profile, preferences, and account settings'
    },
    {
      icon: <Lock className="w-6 h-6 text-orange-600" />,
      title: 'Password Recovery',
      description: 'Quick and secure password reset when you need it most'
    }
  ];

  const authOptions = [
    {
      title: 'New User?',
      description: 'Create your account and start managing renewable energy projects',
      buttonText: 'Sign Up',
      buttonLink: '/signup',
      icon: <UserPlus className="w-8 h-8 text-[#0066A3]" />,
      color: 'blue'
    },
    {
      title: 'Returning User?',
      description: 'Sign in to access your dashboard and continue your work',
      buttonText: 'Sign In',
      buttonLink: '/new-login',
      icon: <LogIn className="w-8 h-8 text-green-600" />,
      color: 'green'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F2F8FC] via-[#F2F8FC] to-[#F2F8FC]">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-[#0066A3] rounded-lg flex items-center justify-center mr-3">
                <span className="text-white text-xl font-bold">A</span>
              </div>
              <h1 className="text-xl font-semibold text-gray-900">AxonMetric</h1>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/new-login"
                className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
              >
                Sign In
              </Link>
              <Link
                to="/signup"
                className="bg-[#0066A3] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1C4D88] transition-colors"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Welcome to{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0066A3] to-[#0066A3]">
              AxonMetric
            </span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Your comprehensive platform for renewable energy project management. 
            Choose your authentication method and start your journey.
          </p>
          
          {/* Demo Account Info */}
          <div className="bg-[#F2F8FC] border border-[#C2DFF1] rounded-lg p-6 mb-12 max-w-2xl mx-auto">
            <div className="flex items-center justify-center mb-4">
              <Zap className="w-6 h-6 text-[#0066A3] mr-2" />
              <h3 className="text-lg font-semibold text-[#0D2747]">Try Demo Accounts</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="bg-white p-3 rounded border">
                <span className="font-medium text-gray-700">Admin:</span> admin / admin123
              </div>
              <div className="bg-white p-3 rounded border">
                <span className="font-medium text-gray-700">User:</span> user / user123
              </div>
            </div>
          </div>
        </div>

        {/* Authentication Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {authOptions.map((option, index) => (
            <div
              key={index}
              className={`bg-white rounded-2xl shadow-lg p-8 border-t-4 border-${option.color}-500 hover:shadow-xl transition-shadow`}
            >
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-6">
                  {option.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{option.title}</h3>
                <p className="text-gray-600 mb-6">{option.description}</p>
                <Link
                  to={option.buttonLink}
                  className={`inline-flex items-center bg-${option.color}-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-${option.color}-700 transition-colors`}
                >
                  {option.buttonText}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Features Section */}
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-12">Why Choose Our Authentication System?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Get Started?</h2>
          <p className="text-gray-600 mb-8">
            Join thousands of professionals managing renewable energy projects with AxonMetric
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup"
              className="bg-[#0066A3] text-white px-8 py-3 rounded-lg font-medium hover:bg-[#1C4D88] transition-colors inline-flex items-center"
            >
              <UserPlus className="w-5 h-5 mr-2" />
              Create Account
            </Link>
            <Link
              to="/new-login"
              className="bg-gray-100 text-gray-700 px-8 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors inline-flex items-center"
            >
              <LogIn className="w-5 h-5 mr-2" />
              Sign In
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-gray-500">
            <p>&copy; 2024 AxonMetric. All rights reserved.</p>
            <div className="mt-2 space-x-4 text-sm">
              <a href="#" className="hover:text-gray-700">Terms of Service</a>
              <a href="#" className="hover:text-gray-700">Privacy Policy</a>
              <a href="#" className="hover:text-gray-700">Support</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthLandingPage;
